'use strict';
/* ==========================================================================
   core-local/ratio.js — "Straight Through Zero": the proportional-relationship model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/ratiocheck.js, tools/contentcheck.js) and in
   the page (window.Ratio). Registers itself with the question bank as `ratio` when core/bank.js is loaded first
   (lesson.json scripts order), so a pool expression may say ratio.isProp(3, 3 * p, 4, 4 * p).

   EXACT ARITHMETIC: every ratio is a reduced fraction {n, d} of integers (d > 0). A decimal input (2.5, 0.25, 3.2) is
   converted exactly through its decimal string (3.2 → 32/10 → 16/5), never through float division. Money is integer
   cents (a board row carries `cents` when the board is money). Numbers only leave the model as numbers at the edge
   (toNumber) or as text (text / money).

   ---- fractions ----
   Ratio.frac(v)                 → {n, d}   from a number (exact via its decimal string), an int, or {n, d}
   Ratio.of(y, x)                → {n, d}   y ÷ x exactly (null when x = 0)
   Ratio.eq(a, b)                → a = b exactly (numbers or fractions)
   Ratio.add / sub / mul / div   → {n, d}
   Ratio.toNumber(q), Ratio.isInt(q), Ratio.terminates(q)
   Ratio.text(q)                 → "2.5", "12", "0.25", "about 8.33" (a non-terminating ratio, only for cheat rows)
   ---- proportionality ----
   Ratio.isProportional(points)  → {ok, p, pq, why, row}. points = [[x, y], ...] or [{x, y}, ...].
                                    ok: every x ≠ 0 pair has the same y ÷ x and every x = 0 pair has y = 0.
                                    p (number) / pq (fraction): the board's constant (the ratio most rows share; ties →
                                    the first row's). why: 'same-ratio' | 'ratio-changes' | 'fee' (the points are on a
                                    straight line that would hit (0, b ≠ 0), or an x = 0 row with y ≠ 0).
                                    row: the index of the first pair that breaks it (−1 when ok).
   Ratio.isProp(x1, y1, x2, y2, …) → boolean (the same test, flat arguments: usable in a bank expression)
   Ratio.isStraight(points)      → the points lie on one line (constant Δy ÷ Δx)
   Ratio.interceptOf(points)     → the y where x = 0 by the table's own constant differences (number), or null when
                                    the table isn't straight
   Ratio.line({p, b})            → {p, b, y(x), points(xs), isProportional}   (y = p x + b, exact)
   ---- Price Tags boards (content/bank.tags.json) ----
   Ratio.CHEATS                  → ['additive', 'fee', 'bulk', 'slip', 'offLine']   (+ 'legit' = no cheat)
   Ratio.board(item, params)     → {kind, title, xLabel, yLabel, money, cheat, bad, p, pq, fee, off, yStep, grid,
                                     rows: [{x, y, yq, cents, text, honest, broken}], honest: [same, no cheat]}
                                    The honest y = p · x; the cheat changes exactly row `bad` (bank.tags.json notes):
                                    additive  y[bad] = y[bad−1] + (x[bad] − x[bad−1])
                                    fee       y[bad] = p·x[bad] + fee
                                    bulk      y[bad] = p·x[bad] − off        (bad is the LAST row)
                                    slip      y[bad] = p·(x[bad] + 1)
                                    offLine   y[bad] = p·x[bad] + one y grid step (scaleFor of the honest points)
   Ratio.rowWorking(board, i)    → "$36.00 ÷ 3 = 12" (what a tap on row i shows; the value from the model)
   ---- graphs (Plot It) ----
   Ratio.scaleFor(points, {yStep, max})  → the y step per square in STEPS = [1, 2, 5, 10, 20, 50, 100] so that EVERY
                                    point's y is on a grid line, preferring the smallest that fits in `max` (12)
                                    squares; an explicit yStep is respected (and checked by tools/ratiocheck.js)
   Ratio.grid(points, opts)      → {xStep: 1, yStep, xMax, yMax, xSquares, ySquares}  (one square of headroom)
   Ratio.snap(tap, grid)         → {x, y} the nearest grid point (data units), clamped to the grid
   ---- the bank side (the SHARED evaluator: core/bank.js; nothing re-implemented) ----
   Ratio.num(v)                  → v rounded to 2 decimals (the "num" format's value)
   Ratio.format(v, fmt)          → "num" = up to 2 decimals, trailing zeros trimmed (−, no unit); else Bank.format
   Ratio.quantize(v, fmt)        → the value a typed answer is compared at (num: hundredths; else Bank.quantize)
   Ratio.fill(text, params)      → Bank.fill, also accepting {expr|num}
   Ratio.eval(expr, params)      → Bank.eval, also accepting array LITERALS ([[3, 3 * p], [4, 4 * p]]) — rewritten to
                                    ratio.arr(…) calls before the shared whitelist sees them (computed access a[i] is
                                    still refused)
   Ratio.truth(expr, params)     → Ratio.eval that must give true/false (an object such as isProportional(…)'s result
                                    is an error: say .ok)
   Ratio.steps(item, params)     → the item's steps, filled and computed: [{type, text, hint, format, value, answerText,
                                    point: {x, y}, options, correct, truth}] (the last step is the answer)
   Ratio.check(step, value)      → is the typed value / tapped {x, y} / option index right?
   Ratio.pool(json)              → a copy of a pool the shared core/bank.js accepts (see normalize below): the games
                                    pass THIS to Bank.attempt / Bank.draw
   Ratio.plotGrid(item, params)  → the Plot It grid for an item (its points, plotted answers, table rows; graph.yStep)
   ========================================================================== */
(function (root) {
  /* ---------------- exact fractions ---------------- */
  const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
  function mk(n, d) {
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d) || d === 0) throw new Error(`ratio: bad fraction ${n}/${d}`);
    if (d < 0) { n = -n; d = -d; }
    const k = gcd(n, d);
    return { n: n / k, d: d / k };
  }
  /* a number → its exact fraction via its shortest decimal string (2.5 → 5/2, 3.2 → 16/5, 0.1 → 1/10) */
  function frac(v) {
    if (v && typeof v === 'object' && 'n' in v && 'd' in v) return mk(v.n, v.d);
    if (typeof v === 'string') v = +v;
    if (typeof v !== 'number' || !isFinite(v)) throw new Error(`ratio: ${v} is not a finite number`);
    if (Number.isInteger(v)) return mk(v, 1);
    const s = String(v);
    if (/e/i.test(s)) throw new Error(`ratio: ${v} is too small or large for an exact fraction`);
    const neg = s[0] === '-', [ip, fp = ''] = s.replace('-', '').split('.');
    const d = Math.pow(10, fp.length), n = +ip * d + +fp;
    return mk(neg ? -n : n, d);
  }
  const F = frac;
  const add = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d + b.n * a.d, a.d * b.d); };
  const sub = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d - b.n * a.d, a.d * b.d); };
  const mul = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.n, a.d * b.d); };
  const div = (a, b) => { a = F(a); b = F(b); if (b.n === 0) throw new Error('ratio: ÷ 0'); return mk(a.n * b.d, a.d * b.n); };
  const eq = (a, b) => { if (a == null || b == null) return a == null && b == null; a = F(a); b = F(b); return a.n === b.n && a.d === b.d; };
  const toNumber = q => { q = F(q); return q.n / q.d; };
  const isInt = q => F(q).d === 1;
  const isZero = q => F(q).n === 0;
  /* a terminating decimal: the reduced denominator has only 2s and 5s */
  function terminates(q) { let d = F(q).d; while (d % 2 === 0) d /= 2; while (d % 5 === 0) d /= 5; return d === 1; }
  /* y ÷ x, exact (null when x = 0) */
  const of = (y, x) => (isZero(x) ? null : div(y, x));
  /* exact decimal text of a terminating fraction ("2.5", "−0.25", "12"); non-terminating: "about 8.33" */
  function decText(q) {
    q = F(q);
    if (!terminates(q)) return 'about ' + trim2(q.n / q.d);
    const neg = q.n < 0, n = Math.abs(q.n), ip = Math.floor(n / q.d);
    let r = n % q.d, fp = '';
    for (let i = 0; r && i < 12; i++) { r *= 10; fp += Math.floor(r / q.d); r %= q.d; }
    return (neg ? '−' : '') + ip + (fp ? '.' + fp : '');
  }
  const trim2 = v => { const s = roundTo(v, 2).toFixed(2).replace(/\.?0+$/, ''); return s.replace(/^-/, '−'); };
  function roundTo(x, d = 0) {                            // half away from zero, float noise dropped (as core/bank.js)
    x = +x; if (!isFinite(x)) return x;
    const f = Math.pow(10, d), u = Number((Math.abs(x) * f).toPrecision(12));
    return Math.sign(x) * Math.round(u) / f;
  }
  /* integer cents of an exact dollar amount (throws when it isn't a whole number of cents) */
  function centsOf(q) { const c = mul(q, 100); if (c.d !== 1) throw new Error(`ratio: ${decText(q)} is not a whole number of cents`); return c.n; }
  function moneyText(q) { const c = centsOf(q), a = Math.abs(c); return (c < 0 ? '−' : '') + '$' + String(Math.floor(a / 100)).replace(/\B(?=(\d{3})+$)/g, ',') + '.' + String(a % 100).padStart(2, '0'); }

  /* ---------------- proportionality ---------------- */
  const pt = p => (Array.isArray(p) ? { x: F(p[0]), y: F(p[1]) } : { x: F(p.x), y: F(p.y) });
  function isStraight(points) {
    const P = points.map(pt);
    if (P.length < 2) return true;
    let slope = null;
    for (let i = 1; i < P.length; i++) {
      const dx = sub(P[i].x, P[0].x), dy = sub(P[i].y, P[0].y);
      if (isZero(dx)) { if (!isZero(dy)) return false; continue; }
      const s = div(dy, dx);
      if (slope == null) slope = s; else if (!eq(s, slope)) return false;
    }
    return true;
  }
  /* the y at x = 0 of a straight table, by its own constant differences (null if not straight / no slope) */
  function interceptQ(points) {
    const P = points.map(pt);
    if (!isStraight(P.map(q => [q.x, q.y]))) return null;
    const z = P.find(q => isZero(q.x));
    if (z) return z.y;
    const a = P[0], b = P.find(q => !eq(q.x, a.x));
    if (!b) return null;
    const s = div(sub(b.y, a.y), sub(b.x, a.x));
    return sub(a.y, mul(s, a.x));
  }
  const interceptOf = points => { const q = interceptQ(points); return q == null ? null : toNumber(q); };
  function isProportional(points) {
    const P = points.map(pt);
    // the board's constant: the ratio most x ≠ 0 rows share (ties → the earliest row's)
    const counts = new Map(); let pq = null, best = 0;
    P.forEach(q => { if (isZero(q.x)) return; const r = div(q.y, q.x), k = r.n + '/' + r.d; counts.set(k, (counts.get(k) || 0) + 1); });
    for (const q of P) {
      if (isZero(q.x)) continue;
      const r = div(q.y, q.x), c = counts.get(r.n + '/' + r.d);
      if (c > best) { best = c; pq = r; }
    }
    let row = -1;
    for (let i = 0; i < P.length && row < 0; i++) {
      const q = P[i];
      if (isZero(q.x)) { if (!isZero(q.y)) row = i; } else if (!eq(div(q.y, q.x), pq)) row = i;
    }
    if (row < 0) return { ok: true, p: pq ? toNumber(pq) : null, pq, why: 'same-ratio', row: -1 };
    const b = interceptQ(P.map(q => [q.x, q.y]));
    const why = (b != null && !isZero(b)) || (isZero(P[row].x) && !isZero(P[row].y)) ? 'fee' : 'ratio-changes';
    return { ok: false, p: pq ? toNumber(pq) : null, pq, why, row, b: b == null ? null : toNumber(b) };
  }
  /* flat form for bank expressions: ratio.isProp(3, 3 * p, 4, 4 * p) → boolean */
  function isProp(...xy) {
    if (xy.length < 2 || xy.length % 2) throw new Error('ratio.isProp: x, y pairs');
    const pts = []; for (let i = 0; i < xy.length; i += 2) pts.push([xy[i], xy[i + 1]]);
    return isProportional(pts).ok;
  }
  /* y = p x + b, exact */
  function line({ p, b = 0 }) {
    const pq = F(p), bq = F(b);
    const yq = x => add(mul(pq, x), bq);
    return { p: toNumber(pq), b: toNumber(bq), pq, bq, yq, y: x => toNumber(yq(x)), points: xs => xs.map(x => [x, toNumber(yq(x))]), isProportional: isZero(bq) };
  }

  /* ---------------- grids ---------------- */
  const STEPS = [1, 2, 5, 10, 20, 50, 100];
  const onGrid = (y, step) => isInt(div(y, step));
  function scaleFor(points, opts = {}) {
    const ys = points.map(p => pt(p).y);
    const maxY = ys.reduce((m, y) => Math.max(m, toNumber(y)), 0);
    if (opts.yStep != null) return +opts.yStep;
    const max = opts.max || 12;
    const valid = STEPS.filter(s => ys.every(y => onGrid(y, s)));
    if (!valid.length) return null;                       // no step puts every point on a line (e.g. y = 2.5): 1 is closest
    return valid.find(s => Math.ceil(maxY / s) <= max) || valid.find(s => Math.ceil(maxY / s) <= 20) || valid[valid.length - 1];
  }
  function grid(points, opts = {}) {
    const P = points.map(pt);
    const yStep = scaleFor(points, opts) || 1;
    const maxX = P.reduce((m, q) => Math.max(m, toNumber(q.x)), 0), maxY = P.reduce((m, q) => Math.max(m, toNumber(q.y)), 0);
    const xSquares = Math.max(opts.minX || 5, Math.ceil(maxX) + 1);
    const ySquares = Math.max(opts.minY || 4, Math.ceil(maxY / yStep) + 1);
    return { xStep: 1, yStep, xMax: xSquares, yMax: ySquares * yStep, xSquares, ySquares };
  }
  function snap(tap, G) {
    const xs = G.xStep || 1, ys = G.yStep || 1;
    const x = Math.max(0, Math.min(G.xMax, Math.round(tap.x / xs) * xs));
    const y = Math.max(0, Math.min(G.yMax, Math.round(tap.y / ys) * ys));
    return { x: roundTo(x, 6), y: roundTo(y, 6) };
  }

  /* ---------------- Price Tags boards ---------------- */
  const CHEATS = ['additive', 'fee', 'bulk', 'slip', 'offLine'];
  const valueOf = (v, params) => {
    if (typeof v === 'number') return v;
    const k = String(v).trim();
    if (Object.prototype.hasOwnProperty.call(params, k)) return +params[k];
    if (/^-?\d+(\.\d+)?$/.test(k)) return +k;
    return +evalExpr(k, params);
  };
  function board(item, params = {}) {
    const cheat = item.cheat || 'legit';
    if (cheat !== 'legit' && !CHEATS.includes(cheat)) throw new Error(`ratio.board: ${item.id}: unknown cheat "${cheat}"`);
    const pq = F(valueOf(params.p != null ? 'p' : item.p, params));
    const xs = item.xs.map(x => F(valueOf(x, params)));
    const honestY = xs.map(x => mul(pq, x));
    const yStep = scaleFor(xs.map((x, i) => [x, honestY[i]]), { yStep: item.yStep != null ? valueOf(item.yStep, params) : undefined });
    const ys = honestY.slice();
    let bad = -1;
    const fee = params.fee != null ? F(params.fee) : null, off = params.off != null ? F(params.off) : null;
    if (cheat !== 'legit') {
      bad = +params.bad;
      if (!(Number.isInteger(bad) && bad >= 0 && bad < xs.length)) throw new Error(`ratio.board: ${item.id}: bad = ${params.bad} is not a row`);
      if (cheat === 'additive') { if (bad < 1) throw new Error(`ratio.board: ${item.id}: additive needs a row before bad`); ys[bad] = add(ys[bad - 1], sub(xs[bad], xs[bad - 1])); }
      else if (cheat === 'fee') ys[bad] = add(honestY[bad], fee);
      else if (cheat === 'bulk') ys[bad] = sub(honestY[bad], off);
      else if (cheat === 'slip') ys[bad] = mul(pq, add(xs[bad], 1));
      else if (cheat === 'offLine') ys[bad] = add(honestY[bad], yStep || 1);
    }
    const money = !!item.money;
    const row = (x, yq, i, honest) => ({ x: toNumber(x), xq: x, y: toNumber(yq), yq, cents: money ? centsOf(yq) : null,
      text: money ? moneyText(yq) : decText(yq), honest, broken: !honest });
    const rows = xs.map((x, i) => row(x, ys[i], i, i !== bad));
    const honest = xs.map((x, i) => row(x, honestY[i], i, true));
    const all = rows.map(r => [r.xq, r.yq]);
    return { id: item.id, kind: item.kind || 'table', title: item.title, xLabel: item.x, yLabel: item.y, money, cheat, bad,
      p: toNumber(pq), pq, fee: fee && toNumber(fee), off: off && toNumber(off), yStep,
      grid: grid(all, { yStep: yStep || undefined }), rows, honest };
  }
  /* what a tap on row i shows: "$36.00 ÷ 3 = 12", "25 ÷ 3 = about 8.33" */
  function rowWorking(b, i) {
    const r = b.rows[i];
    if (!r) return '';
    if (isZero(r.xq)) return `${r.text} at 0`;
    return `${r.text} ÷ ${decText(r.xq)} = ${decText(div(r.yq, r.xq))}`;
  }

  /* ---------------- the bank side: the SHARED evaluator (core/bank.js) ---------------- */
  /* the shared bank (the page: window.Bank; node: required on first use), with `ratio` registered on it */
  function BANK() {
    let B = root.Bank;
    if (!B && typeof require === 'function') B = require('../../../shared/engine/v2/core/bank.js');
    if (!B) throw new Error('ratio: core/bank.js is not loaded');
    if (!B.registered().includes('ratio')) B.register('ratio', Ratio);
    return B;
  }
  const num = v => roundTo(v, 2);
  function format(v, fmt, unit) {
    if (fmt === 'num') { const s = typeof v === 'number' ? trim2(v) : String(v); return unit ? s + ' ' + unit : s; }
    return BANK().format(v, fmt || 'text', unit);
  }
  function quantize(v, fmt) { return fmt === 'num' ? roundTo(+v * 100, 0) : BANK().quantize(v, fmt); }
  /* {expr|num} → {expr} (the shared bank's bare {expr} already prints up to 2 decimals, trailing zeros trimmed) */
  const unNum = text => String(text).replace(/\{([^{}]+?)\|\s*num\s*\}/g, '{$1}');
  function fill(text, params, unit) { return text == null ? '' : BANK().fill(unNum(text), params, unit); }
  /* array literals → ratio.arr(…): '[' is a literal only after an operator, '(' ',' or the start; a '[' after a name,
     number, ')' or ']' would be computed access (still refused by the shared whitelist) */
  function arrays(expr) {
    const s = String(expr);
    if (s.indexOf('[') < 0) return s;
    let out = '', prev = '';
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (c === "'" || c === '"') { const j = s.indexOf(c, i + 1); out += s.slice(i, j + 1); i = j; prev = 'x'; continue; }
      if (c === '[') {
        if (/[\w$)\].]/.test(prev)) throw new Error(`ratio: "${s}": computed access [ ] is not allowed`);
        out += 'ratio.arr('; prev = '(';
      } else if (c === ']') { out += ')'; prev = ']'; } else { out += c; if (!/\s/.test(c)) prev = c; }
    }
    return out;
  }
  function evalExpr(expr, params = {}) { return BANK().eval(arrays(expr), params); }
  function truth(expr, params = {}) {
    const v = evalExpr(expr, params);
    if (typeof v !== 'boolean') throw new Error(`ratio: truth "${expr}" gives ${v && typeof v === 'object' ? 'an object (add .ok?)' : JSON.stringify(v)}, not true/false`);
    return v;
  }
  const arr = (...a) => a;

  /* the Plot It steps of an item, filled and computed from its params */
  function steps(item, params) {
    return (item.steps || []).map((s, i) => {
      const o = { i, type: s.type, text: fill(s.text, params), hint: s.hint ? fill(s.hint, params) : '', format: s.format || null, last: i === item.steps.length - 1 };
      if (s.type === 'numpad') { o.value = evalExpr(s.formula, params); o.answerText = format(o.value, s.format || 'num'); }
      else if (s.type === 'plot') { o.point = { x: +evalExpr(String(s.x), params), y: +evalExpr(String(s.y), params) }; o.answerText = `(${trim2(o.point.x)}, ${trim2(o.point.y)})`; }
      else if (s.type === 'choice') { o.options = (s.options || []).map(t => fill(t, params)); o.correct = s.correct; o.answerText = o.options[o.correct]; if (s.truth) o.truth = truth(s.truth, params); }
      else throw new Error(`ratio.steps: ${item.id} step ${i + 1}: unknown type "${s.type}"`);
      return o;
    });
  }
  function parseTyped(v) {
    if (typeof v === 'number') return v;
    const s = String(v).replace(/[,\s$%]/g, '').replace(/−/g, '-');
    return /^-?(\d+\.?\d*|\.\d+)$/.test(s) ? +s : NaN;
  }
  function check(step, value) {
    if (step.type === 'numpad') { const v = parseTyped(value); return isFinite(v) && quantize(v, step.format || 'num') === quantize(step.value, step.format || 'num'); }
    if (step.type === 'plot') return !!value && Math.abs(value.x - step.point.x) < 1e-9 && Math.abs(value.y - step.point.y) < 1e-9;
    if (step.type === 'choice') return value === step.correct;
    return false;
  }
  /* the grid a Plot It item is drawn on: every point it shows or asks for is on a grid line (graph.yStep respected).
     On-grid points: graph.points, the table's rows, every plot step's answer, each graph line's y-intercept (0, b) when
     b ≠ 0 (a step may ask for it), and the y-intercept of the line through the first two plot-step answers when it is
     not 0 (the game draws that line back to the y axis and circles where it lands). The lines are drawn from x = 0
     across the grid and CLIPPED at its top edge; the grid gets extra squares (the y step unchanged, at most 16 tall) so
     each graph line is visible out to x = 3 (or the grid's end). */
  const LINE_X = 3, MAX_SQUARES = 16;
  function plotPoints(item, params) {
    const G = item.graph || {}, pts = [];
    const val = v => (typeof v === 'number' ? v : evalExpr(String(v), params));
    (G.points || []).forEach(p => pts.push([val(p[0]), val(p[1])]));
    (G.table || []).forEach(p => { if (p[1] !== '?') pts.push([val(p[0]), val(p[1])]); });
    const plots = [];
    (item.steps || []).forEach(s => { if (s.type === 'plot') { const q = [val(s.x), val(s.y)]; plots.push(q); pts.push(q); } });
    (G.lines || []).forEach(l => { const b = val(l.b == null ? 0 : l.b); if (b) pts.push([0, b]); });
    if (plots.length >= 2 && +plots[0][0] !== +plots[1][0]) { const b = interceptOf(plots.slice(0, 2)); if (b != null && b !== 0) pts.push([0, b]); }
    return pts;
  }
  function plotGrid(item, params) {
    const G = item.graph || {}, pts = plotPoints(item, params);
    const val = v => (typeof v === 'number' ? v : evalExpr(String(v), params));
    const yStep = G.yStep != null ? +val(G.yStep) : undefined;
    const out = { ...grid(pts.length ? pts : [[4, 0]], { yStep }), points: pts };
    const lines = G.lines || [];
    if (lines.length) {
      const xl = Math.min(out.xMax, LINE_X);
      const need = Math.max(...lines.map(l => toNumber(val(l.p)) * xl + toNumber(val(l.b == null ? 0 : l.b))));
      const sq = Math.min(MAX_SQUARES, Math.max(out.ySquares, Math.ceil(need / out.yStep - 1e-9)));
      if (sq !== out.ySquares) { out.ySquares = sq; out.yMax = sq * out.yStep; }
    }
    return out;
  }

  /* ---------------- pools the shared core/bank.js accepts ----------------
     The content uses two things core/bank.js doesn't know (docs/QUESTION_BANK.md §7): the "num" format, and Plot It
     items whose answers live in `steps` (no top-level formula / explain). Until the content (or, with the user's OK,
     core/bank.js) changes, Ratio.pool(json) returns a copy with:
       - format "num" → "text", its formula and distractors wrapped in ratio.num(…) (same printout: ≤ 2 decimals);
       - a step item without a formula: answer "numpad", formula = the last numeric step (numpad formula or plot y),
         format = that step's (num → dec1), explain = the last step's text. The Plot It game NEVER shows these: it uses
         Ratio.steps(). Each copy lists what was changed in `_normalized` (tools/contentcheck.js prints it). */
  function pool(json) {
    const out = JSON.parse(JSON.stringify(json));
    for (const it of out.items || []) {
      const changed = [];
      if (it.format === 'num') {
        it.format = 'text';
        if (typeof it.formula === 'string') it.formula = `ratio.num(${it.formula})`;
        if (it.distractors) for (const k of Object.keys(it.distractors)) it.distractors[k] = `ratio.num(${it.distractors[k]})`;
        changed.push('format num → text + ratio.num(…)');
      }
      if (it.formula === undefined && Array.isArray(it.steps) && it.params) {
        const numeric = it.steps.filter(s => s.type === 'numpad' || s.type === 'plot');
        const last = numeric[numeric.length - 1];
        it.answer = 'numpad';
        it.formula = last ? (last.type === 'numpad' ? last.formula : String(last.y)) : '0';
        it.format = last && last.type === 'numpad' && ['money', 'int'].includes(last.format) ? last.format : 'dec1';
        changed.push(`step item: formula = last numeric step (${it.formula}), format ${it.format}`);
      }
      if (it.explain === undefined && Array.isArray(it.steps) && it.steps.length) { it.explain = unNum(it.steps[it.steps.length - 1].text); changed.push('explain = last step text'); }
      for (const f of ['q', 'explain']) if (typeof it[f] === 'string') it[f] = unNum(it[f]);
      if (changed.length) it._normalized = changed;
    }
    return out;
  }

  const Ratio = {
    frac, of, eq, add, sub, mul, div, toNumber, isInt, terminates, text: decText, money: moneyText, cents: centsOf,
    isProportional, isProp, isStraight, interceptOf, line,
    CHEATS, board, rowWorking,
    STEPS, scaleFor, grid, snap,
    num, arr, format, quantize, fill, eval: evalExpr, truth, steps, check, parseTyped, pool, plotGrid, plotPoints,
  };
  root.Ratio = Ratio;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('ratio', Ratio);
  if (typeof module !== 'undefined' && module.exports) module.exports = Ratio;
})(typeof window !== 'undefined' ? window : globalThis);
